import { Box, Stack, Typography } from "@mui/material"
import { faTableColumns } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useDateTime } from "../../../modules/localization/useDateTime"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { MeetingDetailsOrder } from "../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import { DrawerSection } from "./DrawerSection"

const useStyles = makeStyles()((theme) => ({
    bookingsInner: {
        padding: "0 1rem 1rem",
    },
    bookingTimeRow: {
        padding: ".25rem 0 .5rem",
    },
    bookingTimeLabel: {
        color: theme.palette.tailwind.slate[500],
        flexShrink: 0,
    },
    bookingTimeValue: {
        color: theme.palette.tailwind.slate[900],
    },
    bookingResourceRow: {
        padding: ".75rem 0",
        borderTop: `1px solid ${theme.palette.tailwind.slate[100]}`,
    },
    bookingResourceName: {
        color: theme.palette.tailwind.slate[900],
    },
    bookingResourceSub: {
        color: theme.palette.tailwind.slate[500],
    },
}))

type Props = Readonly<{
    details: MeetingDetailsOrder
    open: boolean
    onToggle: () => void
}>

export function ResourceBookingsSection({ details, open, onToggle }: Props) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const bookingResources = details.bookingDetails?.resources ?? []
    if (bookingResources.length === 0) return null

    const bookingStart = dateTimeFactory.fromApi(details.meetingDetails?.startDateTime ?? "")
    const bookingEnd = dateTimeFactory.fromApi(details.meetingDetails?.endDateTime ?? "")
    const bookingTimeLabel =
        bookingStart.isValid && bookingEnd.isValid
            ? `${dateTimeFactory.formatDate(bookingStart)} ${dateTimeFactory.formatTime(bookingStart)} - ${dateTimeFactory.formatTime(bookingEnd)}`
            : "-"

    return (
        <DrawerSection
            icon={faTableColumns}
            titleId="meeting-service-order-drawer-resource-bookings"
            titleFallback="RESOURCE BOOKINGS"
            count={bookingResources.length}
            open={open}
            onToggle={onToggle}
        >
            <Box className={classes.bookingsInner}>
                <Stack direction="row" alignItems="baseline" spacing=".75rem" className={classes.bookingTimeRow}>
                    <LocalizedStrict id="meeting-service-order-drawer-booking-time">
                        <Typography
                            component="span"
                            variant="caption"
                            fontWeight={700}
                            letterSpacing=".05em"
                            textTransform="uppercase"
                            className={classes.bookingTimeLabel}
                        >
                            BOOKING TIME
                        </Typography>
                    </LocalizedStrict>
                    <Typography component="span" variant="body2" fontWeight={700} className={classes.bookingTimeValue}>
                        {bookingTimeLabel}
                    </Typography>
                </Stack>
                {bookingResources.map((resource, index) => (
                    <Box key={resource.id ?? index} className={classes.bookingResourceRow}>
                        <Typography variant="body2" className={classes.bookingResourceName}>
                            {resource.name}
                        </Typography>
                        {resource.room?.tableSetup?.name && (
                            <Typography variant="body2" className={classes.bookingResourceSub}>
                                {resource.room.tableSetup.name}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </DrawerSection>
    )
}
